// src/pages/company/Contact.jsx
import React, { useState } from "react";
import SEO from "../../components/common/SEO";
import { 
  Mail, 
  MessageSquare, 
  Send, 
  MapPin, 
  Phone, 
  Clock,
  CheckCircle,
  Loader2,
  Sparkles,
  HelpCircle,
  Users,
  Briefcase
} from "lucide-react";
import { toast } from "react-toastify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    category: "general"
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      // Replace with your actual contact form endpoint
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Message sent successfully! We'll get back to you soon.");
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
          category: "general"
        });
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us - VALIDMAIL | Get in Touch"
        description="Have questions about VALIDMAIL? Contact our team for support, sales inquiries, or partnership opportunities. We're here to help with email validation solutions."
        keywords="contact validmail, email validation support, sales inquiry, customer support, partnership"
        canonicalUrl="https://validmail.io/company/contact"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4" />
              Get in Touch
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-4 sm:mb-6">
              We'd love to{" "}
              <span className="text-blue-600 dark:text-blue-400">hear from you</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Have questions about our email validation platform? Our team is here to help you succeed.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {/* Contact Cards */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl sm:rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-800 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mb-4">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">Email Us</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                Send us an email and we'll respond within 24 hours
              </p>
              <a 
                href="mailto:support@validmail.io" 
                className="text-blue-600 dark:text-blue-400 font-semibold text-sm hover:underline"
              >
                support@validmail.io
              </a>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-6 border-2 border-purple-200 dark:border-purple-800 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">Sales Inquiry</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                Interested in enterprise plans? Let's talk
              </p>
              <a 
                href="mailto:sales@validmail.io" 
                className="text-purple-600 dark:text-purple-400 font-semibold text-sm hover:underline"
              >
                sales@validmail.io
              </a>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl sm:rounded-2xl p-6 border-2 border-green-200 dark:border-green-800 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center mb-4">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">Partnerships</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                Explore partnership opportunities with us
              </p>
              <a 
                href="mailto:partners@validmail.io" 
                className="text-green-600 dark:text-green-400 font-semibold text-sm hover:underline"
              >
                partners@validmail.io
              </a>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-slate-200 dark:border-slate-800 p-6 sm:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-6">
                    We've received your message and will get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <Sparkles className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                      Send us a message
                    </h2>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Category Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        I'm inquiring about
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 focus:border-blue-500 focus:outline-none"
                      >
                        <option value="general">General Question</option>
                        <option value="support">Technical Support</option>
                        <option value="sales">Sales & Pricing</option>
                        <option value="partnership">Partnership</option>
                        <option value="feedback">Feedback</option>
                      </select>
                    </div>

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help?"
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        placeholder="Tell us more about your inquiry..."
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 placeholder-slate-400 focus:border-blue-500 focus:outline-none resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 disabled:opacity-60 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* Info Section */}
            <div className="space-y-6">
              {/* Office Info */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-slate-200 dark:border-slate-800 p-6 sm:p-8">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-6">
                  Office Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-slate-50">Address</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        123 Tech Park, Silicon Valley<br />
                        San Francisco, CA 94105<br />
                        United States
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-slate-50">Phone</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        +1 (555) 123-4567
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-slate-50">Business Hours</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Monday - Friday: 9:00 AM - 6:00 PM PST<br />
                        Weekend: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl sm:rounded-3xl border-2 border-amber-200 dark:border-amber-800 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <HelpCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                    Quick Answers
                  </h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Looking for quick answers? Check out our FAQ section for common questions about pricing, features, and integration.
                </p>
                <a
                  href="/faq"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors text-sm"
                >
                  Visit FAQ
                  <Sparkles className="h-4 w-4" />
                </a>
              </div>

              {/* Response Time */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl border-2 border-green-200 dark:border-green-800 p-6">
                <h4 className="font-bold text-slate-900 dark:text-slate-50 mb-2">
                  ⚡ Fast Response Time
                </h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  We typically respond to all inquiries within 24 hours during business days. For urgent matters, please mark your inquiry as "Support" and we'll prioritize it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
